/**
 * Notifications Push Web (PWA) — abonnement côté client.
 * Activé par défaut : on demande la permission dès que possible et on abonne automatiquement.
 * iOS : ne fonctionne qu'en PWA installée (iOS 16.4+).
 */
import { pushAPI } from './api';

const urlBase64ToUint8Array = (base64) => {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(b64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const pushPermission = () => (('Notification' in window) ? Notification.permission : 'unsupported');

/** Abonne l'appareil (suppose la permission accordée). */
const subscribeDevice = async () => {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        const { data } = await pushAPI.vapidPublicKey();
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
    }
    await pushAPI.subscribe({
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent,
    });
    return true;
};

/**
 * Initialise les notifications. Si la permission est déjà accordée → abonne.
 * Si "default" et askIfDefault=true → demande la permission puis abonne.
 */
export const initPush = async ({ askIfDefault = false } = {}) => {
    if (!pushSupported()) return { ok: false, reason: 'unsupported' };
    try {
        let perm = Notification.permission;
        if (perm === 'default' && askIfDefault) {
            perm = await Notification.requestPermission();
        }
        if (perm !== 'granted') return { ok: false, reason: perm };
        await subscribeDevice();
        return { ok: true };
    } catch (e) {
        return { ok: false, reason: 'error', error: e };
    }
};

export const disablePush = async () => {
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            await pushAPI.unsubscribe(sub.endpoint);
            await sub.unsubscribe();
        }
        return true;
    } catch {
        return false;
    }
};
